import { z } from "zod";

/**
 * The HIPAA audit controls (docs/research/2026-09-28-design-hipaa-audit.md): the closed words of
 * the daily chain verification, the write-once export and retention, and the views the web
 * serves from them. Pure and dependency-free beyond zod, so the runner, the server and the client
 * read the same lists.
 *
 * SEC-17: every value here is an identifier, a sequence number, a count, a time, a status from a
 * closed list or a blob path. Never a row's content, a hash of a secret or a URL with a SAS.
 */

/** A chain is the platform trail (`"platform"`) or an instance's trail (its instance id). */
export const PLATFORM_CHAIN = "platform";

export const VERIFY_TRIGGERS = ["schedule", "request"] as const;
export type VerifyTrigger = (typeof VERIFY_TRIGGERS)[number];

export const VERIFY_MODES = ["full", "incremental"] as const;
export type VerifyMode = (typeof VERIFY_MODES)[number];

export const VERIFY_STATUSES = ["running", "ok", "broken", "error"] as const;
export type VerifyStatus = (typeof VERIFY_STATUSES)[number];

/**
 * Why a verification is not `ok`. The first six are a break found in the chain or its anchors
 * (`broken`); `timeout` and `unreachable` mean the check could not run (`error`).
 */
export const VERIFY_REASONS = [
  "hash_mismatch",
  "link_mismatch",
  "head_missing",
  "head_changed",
  "anchor_mismatch",
  "manifest_unreadable",
  "timeout",
  "unreachable",
] as const;
export type VerifyReason = (typeof VERIFY_REASONS)[number];

/** One fixed sentence per reason, for the Integrity tab and the alert. Never a row's content. */
export const VERIFY_REASON_SENTENCES = {
  hash_mismatch: "A row's content no longer matches the hash written when it was recorded.",
  link_mismatch: "A row no longer links to the row before it: a row was removed or reordered.",
  head_missing: "The newest row seen by the previous check is no longer there.",
  head_changed: "The newest row seen by the previous check has changed.",
  anchor_mismatch:
    "The oldest row does not link to the last row of the exported history before it.",
  manifest_unreadable: "The write-once export could not be read back to compare.",
  timeout: "The check did not finish in time.",
  unreachable: "The audit trail could not be reached.",
} as const satisfies Record<VerifyReason, string>;

/** The three runner jobs, each claimed once per UTC day (`audit_job_claims`). */
export const AUDIT_JOBS = ["verify", "export", "retention"] as const;
export type AuditJob = (typeof AUDIT_JOBS)[number];

/**
 * The jobs an audit integrity alert can name: the three above, and the runner's daily tenant
 * grant pairing check (ADR 0010, design §5.5), which is not claimed in `audit_job_claims`.
 */
export const AUDIT_ALERT_JOBS = [...AUDIT_JOBS, "tenant_grants"] as const;
export type AuditAlertJob = (typeof AUDIT_ALERT_JOBS)[number];

/**
 * Why the tenant grant check alerted: a grant the tenant rows do not explain, or no check ran.
 * #1700: `inherited_grant_expiring` is the warning for an allowed inherited grant whose
 * `inheritedGrants` entry ends within its last 7 days (one a placement target a day), and
 * `inherited_grant_expired` an unexpected grant that is only such an entry past its date, so the
 * reader knows it is the known exception ending, not a new grant.
 */
export const TENANT_GRANT_REASONS = [
  "unexpected_grant",
  "check_failed",
  "inherited_grant_expiring",
  "inherited_grant_expired",
] as const;
export type TenantGrantReason = (typeof TENANT_GRANT_REASONS)[number];

export const AUDIT_EXPORT_KINDS = ["day", "seal"] as const;

export const RETENTION_OUTCOMES = ["dropped", "refused", "dry_run"] as const;
export type RetentionOutcome = (typeof RETENTION_OUTCOMES)[number];

/** Why retention kept a month (design "Retention", checks 1 to 5, and the default partition). */
export const RETENTION_REASONS = [
  "inside_window",
  "seal_missing",
  "seal_unreadable",
  "file_mismatch",
  "link_mismatch",
  "count_mismatch",
  "policy_not_locked",
  "verify_not_ok",
  "default_partition_rows",
  "not_configured",
  "error",
] as const;
export type RetentionReason = (typeof RETENTION_REASONS)[number];

/**
 * Six years (45 CFR 164.316(b)(2)(i)): the locked time-based retention the production container
 * must hold before retention drops anything (2,192 days covers two leap days).
 */
export const AUDIT_WORM_RETENTION_DAYS = 2192;

/** The container every audit export, seal and evidence pack is written to. */
export const AUDIT_EXPORT_CONTAINER = "audit-export";

/** What "Verify now" answers. */
export const AUDIT_VERIFY_REQUESTED_MESSAGE =
  "Verification requested. Results appear here within a few minutes.";

/** The platform settings helper under "Audit retention (days)". */
export const AUDIT_RETENTION_HELPER =
  "Audit history is removed a whole month at a time, once that month ended more than this many days ago and its archived copy is verified.";

// ---------------------------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------------------------

const iso = z.string();

/** The newest verification of one chain. */
export const integrityVerificationSchema = z.object({
  id: z.string(),
  trigger: z.enum(VERIFY_TRIGGERS),
  mode: z.enum(VERIFY_MODES),
  status: z.enum(VERIFY_STATUSES),
  reason: z.enum(VERIFY_REASONS).nullable(),
  firstBadSeq: z.string().nullable(),
  rowsChecked: z.number().int().nonnegative(),
  startedAt: iso,
  finishedAt: iso.nullable(),
});
export type IntegrityVerification = z.infer<typeof integrityVerificationSchema>;

/** One row of the Integrity tab. */
export const integrityChainViewSchema = z.object({
  chain: z.string(),
  kind: z.enum(["platform", "instance"]),
  /** "Platform audit trail", or "<tenant> / <instance>". */
  label: z.string(),
  tenantId: z.string().nullable(),
  instanceId: z.string().nullable(),
  lastVerification: integrityVerificationSchema.nullable(),
  /** The newest exported day (UTC date), or null before the first export. */
  lastExportDay: z.string().nullable(),
  /** The oldest hot month ("2026-09"), from the newest verification. */
  oldestHotMonth: z.string().nullable(),
});
export type IntegrityChainView = z.infer<typeof integrityChainViewSchema>;

/**
 * `GET /platform/audit/integrity`. `enabled` is always true since #2501 (owner 2026-11-02: Audit
 * integrity is always on, the `audit.integrity` flag is retired); kept so an older client reads it.
 */
export const platformIntegrityViewSchema = z.object({
  enabled: z.boolean(),
  chains: z.array(integrityChainViewSchema),
  /** A "Verify now" the runner has not picked up yet. */
  pendingRequestAt: iso.nullable(),
});
export type PlatformIntegrityView = z.infer<typeof platformIntegrityViewSchema>;

/** `GET /audit/integrity`: the caller's instance only. */
export const instanceIntegrityViewSchema = z.object({
  enabled: z.boolean(),
  lastVerification: integrityVerificationSchema.nullable(),
});
export type InstanceIntegrityView = z.infer<typeof instanceIntegrityViewSchema>;

/** `POST /platform/audit/integrity/verify`. */
export const verifyRequestViewSchema = z.object({
  requestId: z.string(),
  requestedAt: iso,
  message: z.string(),
});
export type VerifyRequestView = z.infer<typeof verifyRequestViewSchema>;

/** One exported file or seal (`GET /platform/audit/exports`): a path, never a URL. */
export const auditExportViewSchema = z.object({
  id: z.string(),
  chain: z.string(),
  kind: z.enum(AUDIT_EXPORT_KINDS),
  period: z.string(),
  fromSeq: z.string(),
  toSeq: z.string(),
  rowCount: z.number().int().nonnegative(),
  blobPath: z.string(),
  fileSha256: z.string(),
  verified: z.boolean(),
  exportedAt: iso,
});
export type AuditExportView = z.infer<typeof auditExportViewSchema>;

/** How many days of exports the list and the evidence pack cover. */
export const AUDIT_EXPORTS_DAYS = 35;

/** "2026-09" from a timestamp, or null. */
export function monthOf(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 7);
}
